/**
 * The single way to end a session.
 *
 * There were four separate implementations, and they disagreed on everything that
 * matters: where to navigate, whether to tell the server, and whether to reset Apollo.
 * One of them (the user dropdown, the one most people actually click) pushed to
 * '/login' — a route that does not exist — and then called window.location.reload()
 * straight after. The router fell through to its lazy 404 component while the browser
 * was already tearing the page down, the chunk request was aborted, and the rejected
 * import surfaced as a crash screen. That is the bug this replaces.
 *
 * The approach here is a hard navigation instead of a router push. It is the one thing
 * that guarantees no state from the previous user survives: the whole JS context is
 * discarded. That also makes client.cache.reset() unnecessary — resetting the cache
 * while queries are still mounted makes them re-render against empty data, which is its
 * own source of crashes.
 */

import constants from 'config/constants';
import { STORAGE_KEYS, END_REASON } from './session/constants';
import { saveReturnTo, RETURN_TO_KEY } from './session/returnTo';
import { LOGIN_PATH, buildLoginUrl } from './session/authRoutes';
import { markSessionEnded } from './session/sessionEndNotice';

const { HIDDEN_COMPONENT_ON_LTI } = constants;

/**
 * Where an embedded session goes instead. Inside Blackboard the app runs in an
 * iframe and there is no signing in again: the identity comes from the LTI
 * launch, not from our form. Sending that user to the login screen leaves them
 * staring at a form they cannot use, wrapped in their LMS.
 */
const EMBED_ERROR_PATH = '/courses/error';

// Language is a device preference, not session data: clearing it logs the user out AND
// silently switches their UI language, which is not what "sign out" means.
//
// The return destination survives for the same reason it was written a line earlier: it
// describes where to come back to, and wiping it here would erase it between being saved
// and being read on the next sign-in.
//
// HIDDEN_COMPONENT_ON_LTI is on this list for the same reason as the language: it
// describes the mode this browser is in, not the session. Seven places read it to hide
// the navbar, footer and course tabs while embedded, so clearing it makes the
// destination render with full C1DO1 chrome inside Blackboard's frame.
//
// It is preserved unconditionally, and that is the point. Making it conditional on the
// tab that is signing out looks tighter and is wrong: an ordinary tab open beside the
// LMS embed follows the cross-tab announcement into its own logout(), sees no iframe,
// and wipes from shared storage the very flag the embed just saved. A stale 'YES' left
// in an ordinary tab costs nothing — platform.js resets it on mount, which is what that
// branch is for.
const PRESERVED_KEYS = ['locale', 'i18nextLng', RETURN_TO_KEY, HIDDEN_COMPONENT_ON_LTI];

const clearSessionStorageKeepingPreferences = () => {
  const preserved = {};

  PRESERVED_KEYS.forEach((key) => {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) preserved[key] = value;
    } catch {
      // Storage unavailable; nothing to preserve.
    }
  });

  try {
    localStorage.clear();
  } catch {
    // Nothing else to do: the hard navigation below still drops in-memory state.
  }

  Object.entries(preserved).forEach(([key, value]) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Losing the language preference is not worth blocking the sign-out.
    }
  });
};

/**
 * Ends the session and sends the user to the login screen.
 *
 * Never rejects: a failure while signing out must not leave the user stuck in a
 * half-authenticated app. Whatever happens, local state is cleared and we navigate.
 *
 * @param {{ signOutUser?: () => Promise<any>, reason?: string, embedded?: boolean }} [options]
 *   signOutUser: the GraphQL mutation, when the caller has it. Server-side sign-out is
 *   best-effort — the local session is dropped either way.
 *   embedded: the session is ending inside the LTI iframe. Changes where the user lands
 *   and whether the return destination is worth remembering; see EMBED_ERROR_PATH.
 */
export const logout = async ({ signOutUser, reason, embedded = false } = {}) => {
  // Remembered here, and only here, for a session that ended on its own. The route guards
  // save it when a page LOADS without a session, but an expiry mid-work never reaches
  // them: this navigates hard before React renders again. Without this the dialog's
  // promise — "when you sign back in you will pick up where you left off" — held only for
  // the case where the user had already been thrown out.
  //
  // An explicit sign-out is excluded on purpose: someone who chose to leave should land on
  // their own start page, not back inside whatever they were last editing. So is an
  // embedded one: an LTI user comes back through Blackboard's launch, never through our
  // login, so the destination would only sit there and misdirect a later ordinary sign-in.
  if (reason && reason !== END_REASON.LOGOUT && !embedded && typeof window !== 'undefined') {
    saveReturnTo(window.location);
  }

  if (typeof signOutUser === 'function') {
    try {
      await signOutUser();
    } catch (error) {
      // The server may already consider the session gone, or be unreachable. Either
      // way the user asked to leave, so carry on.
      console.warn('signOutUser failed, clearing the session anyway', error);
    }
  }

  // Tell the other tabs before clearing, so they follow this sign-out instead of
  // each discovering it on their own and firing their own request.
  announceEnd();

  clearSessionStorageKeepingPreferences();

  if (typeof window !== 'undefined') {
    // Five screens set window.onbeforeunload while they hold unsaved work
    // (course editing, stage editing, answer upload, feedback). Leaving it armed
    // means an automatic sign-out pops the browser's "Leave site?" dialog, and
    // a user who clicks Cancel stays inside an app whose session is already
    // dead — every query failing, no way out. The session is over either way,
    // so the prompt has nothing left to protect.
    window.onbeforeunload = null;

    // The note that lets the login screen trust `session_ended`. A sign-out on
    // purpose leaves none, and neither does an embedded one: that user never
    // reaches the login screen.
    if (!embedded) markSessionEnded(reason);

    // assign() and not history.push(): a hard navigation drops every bit of in-memory
    // state, and avoids mounting a route (and its lazy chunk) that is about to be
    // thrown away.
    window.location.assign(buildEndUrl(reason, embedded));
  }
};

/**
 * Signals other tabs that this session is over. They pick it up through the
 * 'storage' event and follow, so two tabs cannot end up in incompatible states.
 */
const announceEnd = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.ENDED_AT, String(Date.now()));
  } catch {
    // Cross-tab coordination is a nicety; never block a sign-out for it.
  }
};

/**
 * An explicit sign-out lands on a clean login screen. An expiry carries why, so
 * the screen can say "we closed your session for inactivity" instead of the
 * generic message — different causes deserve different explanations.
 */
const buildEndUrl = (reason, embedded) => {
  // The embedded case carries no reason: /courses/error has one message, and the
  // distinctions the login screen draws (idle vs. the ceiling) exist to tell someone
  // how to sign back in — which is not what this user is about to do.
  if (embedded) return `${EMBED_ERROR_PATH}?expiredSession`;
  // No reason, or a sign-out on purpose, gives the plain login URL.
  return buildLoginUrl(reason);
};

export { LOGIN_PATH, EMBED_ERROR_PATH, PRESERVED_KEYS };
